import { Details } from "~/entities/Details";

// BIK-2148: US territories that UPS XAV reports as a STATE of the US ("state": "PR",
// "countryCode": "US") but that every downstream consumer treats as a country of their
// own. The backend decides domestic-vs-international by comparing country codes
// (BulkOrderProcessor: `isInternational = ShipFromCountryCode != ShipToCountryCode`) and
// checks `Country == "US" || Country == "PR"` side by side, so a shipment sent as US→US
// is priced as domestic and UPS answers "No rates returned".
//
// Production does not hit this because its paste path goes through Google + /replace,
// which already answers `withCountry: "PR"`. The region-paste path added in BIK-2095 asks
// UPS directly, and UPS answers with the territory as a state — hence the regression.
//
// None of these appear in /api/FormData/states under "US" either (verified: the US list
// is 51 entries, the 50 states + DC), so treating them as states leaves the state field
// unresolvable as well. Promoting them to a country resolves both problems at once: the
// rates payload gets the code UPS needs, and `state` stops being required (the API's own
// validator only demands it when CountryCode == "US").
const US_TERRITORY_COUNTRY_CODES = ["PR", "VI", "GU", "AS", "MP"];

/**
 * The country code an address should actually carry, given what AVS returned.
 * Returns the territory code when the answer is a US address whose state is one of the
 * territories above; otherwise the country code is passed through untouched.
 */
function resolveTerritoryCountry(
  countryCode?: string,
  state?: string
): string | undefined {
  if (countryCode !== "US" || !state) return countryCode;
  const code = String(state).trim().toUpperCase();
  return US_TERRITORY_COUNTRY_CODES.includes(code) ? code : countryCode;
}

/**
 * BIK-2148: make `detail.country` safe to write a `.code` onto.
 *
 * A country reaches this function as a full option object, as a raw string ("ES"), or
 * missing entirely — see docs/features/address-data-flow.md §4. Assigning a property to the string
 * form throws, and the caller's catch swallows it along with everything assigned after,
 * so the shape has to be normalised before the write rather than merely tested for
 * truthiness. Any existing string is preserved as the code, since that is what it held.
 */
function ensureCountryObject(detail: Details): void {
  if (detail.country && typeof detail.country !== "string") {
    return;
  }
  const existingCode =
    typeof detail.country === "string" ? (detail.country as string) : "";
  detail.country = {
    active: true,
    code: existingCode,
    id: "",
    name: "",
  } as Details["country"];
}

export default function (response, stageDetail: Details): Details {
  try {
    const validAddress = response.validAddress;
    const originalAddress = response.originalAddress;
    if (validAddress && response.success) {
      if (validAddress.company) {
        stageDetail.company = validAddress.company;
      } else if (originalAddress && originalAddress.company) {
        stageDetail.company = originalAddress.company;
      }
      if (validAddress.address1 && (validAddress.address1 as string).trim()) {
        stageDetail.streetAddress = validAddress.address1.trim();
        stageDetail.address1 = validAddress.address1.trim();
      } else if (
        originalAddress &&
        (originalAddress.address1 as string).trim()
      ) {
        stageDetail.address1 = originalAddress.address1.trim();
      }
      if (validAddress.address2) {
        stageDetail.address2 = validAddress.address2.trim();
      } else if (originalAddress && originalAddress.address2) {
        stageDetail.address2 = originalAddress.address2.trim();
      }
      if (validAddress.address3) {
        stageDetail.address3 = validAddress.address3;
      } else if (originalAddress && originalAddress.address3) {
        stageDetail.address3 = originalAddress.address3;
      }
      if (validAddress.city) {
        stageDetail.city = validAddress.city;
      } else if (originalAddress && originalAddress.city) {
        stageDetail.city = originalAddress.city;
      }
      // BIK-2137: the `else if` assigned into stageDetail.state without checking it
      // exists (unlike its sibling below at the originalAddress-only branch). An
      // international address has no state object at all — Details.setStage resets it
      // to undefined and only re-fills it from allowedStates, which has no entry for
      // countries like Portugal or Taiwan — so this threw a TypeError that the outer
      // catch swallowed, returning a half-populated detail: passedAvs, isChanged,
      // postalCode and countryCode below were all silently lost. That is the same
      // false-negative this ticket removes, reintroduced by the assignment order.
      //
      // Seed the object when it is missing (same shape avsCheck seeds for the region
      // path) so the AVS-parsed state lands instead of being dropped. The state is
      // stored but not displayed: the dropdown still renders only when the country has
      // entries in allowedStates, so nothing new appears for these countries.
      const resolvedState = validAddress.state
        ? validAddress.state
        : originalAddress?.state;
      if (resolvedState) {
        // BIK-2148: `state` can legitimately be a STRING here, not just missing. Details
        // carries it as a raw string on several paths (Details.setStage's <=5-char
        // fallback, a saved address before the lists resolve), and BIK-2137's guard only
        // covered the undefined case — `!stageDetail.state` is false for "MD", so the
        // assignment below ran against a string primitive and threw
        // "Cannot create property 'stateprovcode' on string 'MD'".
        //
        // The throw was invisible: the outer catch returns the half-built detail, and
        // passedAvs is assigned near the end of this function, so an address the API had
        // APPROVED arrived at Ship3 with passedAvs still false — no green badge, an
        // "address could not be validated" warning, and the "Save current address"
        // checkbox (gated on passedAvs) hidden. Seen with a Spanish address, where
        // ServiceObjects answers "Madrid" for the state we sent as "MD": BIK-2136 blanks
        // that unrecognised answer, so resolvedState falls back to the original "MD".
        if (!stageDetail.state || typeof stageDetail.state === "string") {
          stageDetail.state = {
            country: null,
            id: "",
            stateprov: "",
            stateprovcode: "",
          } as Details["state"];
        }
        stageDetail.state.stateprovcode = resolvedState;
      }
      if (validAddress.postalCode) {
        stageDetail.postalCode = validAddress.postalCode;
      } else if (originalAddress && originalAddress.postalCode) {
        stageDetail.postalCode = originalAddress.postalCode;
      }
      // BIK-2148: promote a US territory reported as a state to its own country code —
      // see resolveTerritoryCountry above. Applied here rather than in avsCheck because
      // the rates payload is built from the TRIP, and the trip's country component is
      // written from `detail.country.code` (StoreHelper.reverseMapAddressComponents /
      // Details.mapComponentsToAddress). Normalising at this single write reaches both
      // the detail and the trip; doing it in avsCheck would have fixed only the detail.
      if (validAddress.countryCode) {
        // BIK-2148: `country` has the same string-vs-object duality as `state` (see the
        // note there). The previous `&& stageDetail.country` guard passed for a string
        // like "ES" and then threw on the assignment, taking the whole function — and
        // passedAvs with it — into the catch below.
        ensureCountryObject(stageDetail);
        stageDetail.country.code =
          resolveTerritoryCountry(
            validAddress.countryCode,
            validAddress.state
          ) ?? validAddress.countryCode;
      } else if (originalAddress && originalAddress.countryCode) {
        ensureCountryObject(stageDetail);
        stageDetail.country.code =
          resolveTerritoryCountry(
            originalAddress.countryCode,
            originalAddress.state
          ) ?? originalAddress.countryCode;
      }
    } else if (originalAddress) {
      if (originalAddress.company) {
        stageDetail.company = originalAddress.company;
      }
      if (
        originalAddress.address1 &&
        (originalAddress.address1 as string).trim()
      ) {
        stageDetail.streetAddress = originalAddress.address1.trim();
        stageDetail.address1 = originalAddress.address1.trim();
      }
      if (originalAddress.address2) {
        stageDetail.address2 = originalAddress.address2.trim();
      }
      if (originalAddress.address3) {
        stageDetail.address3 = originalAddress.address3;
      }
      if (originalAddress.city) {
        stageDetail.city = originalAddress.city;
      }
      if (originalAddress.state) {
        // BIK-2148: same string-state guard as the validAddress branch above.
        if (!stageDetail.state || typeof stageDetail.state === "string") {
          stageDetail.state = {
            country: null,
            id: "",
            stateprov: "",
            stateprovcode: "",
          } as Details["state"];
        }
        stageDetail.state.stateprovcode = originalAddress.state;
      }
      if (originalAddress.postalCode) {
        stageDetail.postalCode = originalAddress.postalCode;
      }
      if (originalAddress.countryCode) {
        // BIK-2148: same territory promotion and string guard as the branch above.
        ensureCountryObject(stageDetail);
        stageDetail.country.code =
          resolveTerritoryCountry(
            originalAddress.countryCode,
            originalAddress.state
          ) ?? originalAddress.countryCode;
      }
    }
    // flags
    if (response.success !== undefined) {
      stageDetail.success = response.success;
    }
    if (response.passedAvs !== undefined) {
      stageDetail.passedAvs = response.passedAvs;
    }
    if (response.errorCode !== undefined) {
      stageDetail.errorCode = response.errorCode;
    }
    if (response.errorMessage !== undefined) {
      stageDetail.errorMessage = response.errorMessage;
    }
    if (response.isChanged !== undefined) {
      stageDetail.isChanged = response.isChanged;
    }
    if (response.isResidential !== undefined) {
      stageDetail.isResidential = response.isResidential;
    }
    if (response.validationLevel !== undefined) {
      stageDetail.validationLevel = response.validationLevel;
    }
    if (response.validationSource !== undefined) {
      stageDetail.validationSource = response.validationSource;
    }
    return stageDetail;
  } catch (e) {
    return stageDetail;
  }
}
